// Prism-Funnel — Mock-Daten & Ableitungen.
//
// 1:1-Port der Component-Klasse aus dem Design-Prototyp
// (prism-trial-paywall.dc.html, Zeilen 1230–1365). Reine Fixtures + pure
// Helper — keine React-Abhängigkeit, keine Backend-Calls. Der Funnel unter
// /dev/prism-demo ist standalone und self-contained (Stufe 1).
//
// i18n: user-sichtbare Labels laufen über t() aus @cortex/i18n (liest den
// LangStore, funktioniert auch außerhalb von React). WICHTIG: alle Builder
// übersetzen zur AUFRUFZEIT — Konstanten, die beim Modul-Load evaluieren,
// würden die Sprache einfrieren. Daher sind QUESTIONS/ANALYZE_STEPS jetzt
// Funktionen (questions() / analyzeSteps()).

import { t } from '@cortex/i18n';

// ─── Typen ────────────────────────────────────────────────────────────────

/** Ein Schritt in einer Lade-Sequenz (Analyze / Search). */
export interface FunnelStep {
  /** Schritt-Label. */
  t: string;
  /** Optionale Kennzahl rechts (z.B. "3.412 Firmen"). Leer = keine. */
  n: string;
}

/** Ein Mock-Lead — Firma + Ansprechpartner. */
export interface Lead {
  /** Avatar-Initialen. */
  in: string;
  /** Firmenname. */
  co: string;
  /** Meta-Zeile (Branche · Ort · Größe). */
  meta: string;
  /** Name des Ansprechpartners. */
  name: string;
  /** Rolle des Ansprechpartners. */
  role: string;
  /** Verifizierte E-Mail (nur beim Frei-Lead gesetzt). */
  email?: string;
  /** Direktdurchwahl (nur beim Frei-Lead gesetzt). */
  phone?: string;
  /** Anzahl Ansprechpartner. */
  ap: number;
  /** Optionale Domain (sonst aus E-Mail/Firmenname abgeleitet). Für Favicon. */
  domain?: string;
  /** Optionaler Fit-Score (sonst deterministisch abgeleitet, premium 87–97). */
  score?: number;
  /** Optionale Fit-Begründung (echte Engine liefert sie; Mock hat keine). */
  fitReason?: string;
  /** Wörtlicher Auszug von der Website der Firma (Engine 2.2); im Demo-Set fiktiv. */
  evidence?: string;
}

/** Initialen aus einem Firmennamen (für Avatar-Fallback bei echten Firmen). */
export function initials(name: string): string {
  const parts = String(name || '')
    .replace(/\b(GmbH|AG|KG|OHG|UG|mbH|e\.?K\.?|Co|&)\b/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '★';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Ein Balken im Saisonalitäts-Chart. */
export interface MonthBar {
  /** Kurz-Label (Jan, Feb, …). */
  label: string;
  /** Balkenhöhe in Prozent (z.B. "76%"). */
  hpct: string;
  /** CSS-Klasse des Balken-Wrappers ('wu-mbar' bzw. 'wu-mbar now'). */
  cls: string;
  /** Caption über dem Peak-Balken ('Jetzt' beim aktuellen Monat, sonst ''). */
  cap: string;
}

/** Schlüssel der nischen-adaptiven Lead-Sets. */
export type LeadSetKey = 'steuer' | 'agentur' | 'handwerk' | 'generic';

// ─── Fixtures ─────────────────────────────────────────────────────────────

/** Eine Feinschliff-Frage mit Antwort-Chips (Janik 03.07.: die Antworten müssen
 *  die Frage wörtlich beantworten — kein generisches Ja/Nein). */
export type FunnelQuestion = { q: string; options: string[] };

/** Die 3 KI-Rückfragen im Feinschliff-Schritt (Fallback ohne Engine). */
export function questions(): FunnelQuestion[] {
  return [
    {
      q: t('prism.questions.q1'),
      options: [t('prism.questions.q1o1'), t('prism.questions.q1o2'), t('prism.questions.q1o3')],
    },
    {
      q: t('prism.questions.q2'),
      options: [t('prism.questions.q2o1'), t('prism.questions.q2o2'), t('prism.questions.q2o3')],
    },
    {
      q: t('prism.questions.q3'),
      options: [t('prism.questions.q3o1'), t('prism.questions.q3o2'), t('prism.questions.q3o3')],
    },
  ];
}

/** Schritte der Website-Analyse. */
export function analyzeSteps(): FunnelStep[] {
  return [
    { t: t('prism.analyze.step1'), n: '' },
    { t: t('prism.analyze.step2'), n: '' },
    { t: t('prism.analyze.step3'), n: '' },
    { t: t('prism.analyze.step4'), n: '' },
  ];
}

/**
 * Nischen-adaptive Lead-Daten — die Vorschau (Ergebnis + Paywall-Tresor)
 * passt zur gewählten Zielgruppe. Nur der jeweils erste Lead trägt
 * E-Mail + Telefon (der "geht auf uns"-Frei-Lead).
 */
export const LEAD_SETS: Record<LeadSetKey, Lead[]> = {
  steuer: [
    {
      in: 'SB',
      co: 'Steuerkanzlei Berger & Partner',
      meta: 'Steuerberatung · München · 40–80 Mitarbeitende',
      name: 'Dr. Thomas Berger',
      role: 'Gesch. Partner',
      email: 'berger@kanzlei-berger.de',
      phone: '+49 89 21 30 88-0',
      ap: 12,
    },
    {
      in: 'HS',
      co: 'Hoffmann Steuerberatung',
      meta: 'Steuerberatung · Nürnberg · 10–40 Mitarbeitende',
      name: 'Sabine Hoffmann',
      role: 'Inhaberin',
      ap: 7,
    },
    {
      in: 'RK',
      co: 'Reiter & Kollegen WP/StB',
      meta: 'Wirtschaftsprüfung · Augsburg · 80–150 Mitarbeitende',
      name: 'Michael Reiter',
      role: 'Partner',
      ap: 21,
    },
    {
      in: 'SL',
      co: 'Steuerbüro Lindner',
      meta: 'Steuerberatung · Regensburg · 10–40 Mitarbeitende',
      name: 'Andreas Lindner',
      role: 'Inhaber',
      ap: 5,
    },
    {
      in: 'WH',
      co: 'Kanzlei Wagner & Huber',
      meta: 'Steuerberatung · Ingolstadt · 20–50 Mitarbeitende',
      name: 'Petra Wagner',
      role: 'Partnerin',
      ap: 9,
    },
  ],
  agentur: [
    {
      in: 'PW',
      co: 'Pixelwerk Kreativagentur',
      meta: 'Marketing-Agentur · Köln · 20–50 Mitarbeitende',
      name: 'Lena Brinkmann',
      role: 'Geschäftsführerin',
      email: 'l.brinkmann@pixelwerk.de',
      phone: '+49 221 56 02 14-0',
      ap: 8,
    },
    {
      in: 'NM',
      co: 'Nordlicht Media GmbH',
      meta: 'Performance-Marketing · Düsseldorf · 30–60 Mitarbeitende',
      name: 'Jonas Vetter',
      role: 'Inhaber',
      ap: 11,
    },
    {
      in: 'SD',
      co: 'Studio Drei Kreativ',
      meta: 'Branding & Design · Essen · 10–25 Mitarbeitende',
      name: 'Carla Sommer',
      role: 'Creative Director',
      ap: 6,
    },
    {
      in: 'RD',
      co: 'Reichweite Digital',
      meta: 'Online-Marketing · Dortmund · 15–40 Mitarbeitende',
      name: 'Tim Hassel',
      role: 'Geschäftsführer',
      ap: 9,
    },
    {
      in: 'KW',
      co: 'Konvex Werbeagentur',
      meta: 'Werbeagentur · Bochum · 20–45 Mitarbeitende',
      name: 'Miriam Pohl',
      role: 'Partnerin',
      ap: 7,
    },
  ],
  handwerk: [
    {
      in: 'EP',
      co: 'Elektro Petersen GmbH',
      meta: 'Elektrotechnik · Hamburg · 25–60 Mitarbeitende',
      name: 'Sven Petersen',
      role: 'Inhaber',
      email: 's.petersen@elektro-petersen.de',
      phone: '+49 40 38 60 47-0',
      ap: 6,
      evidence: 'Ihr Meisterbetrieb für Elektroinstallation, Photovoltaik und Wallboxen in Hamburg',
    },
    {
      in: 'BN',
      co: 'Bäder Manufaktur Nord',
      meta: 'Sanitär & Heizung · Hamburg · 15–40 Mitarbeitende',
      name: 'Heiko Brandt',
      role: 'Geschäftsführer',
      ap: 8,
      evidence: 'Komplettbäder aus einer Hand – Planung, Sanitär, Fliesen und Elektro',
    },
    {
      in: 'DO',
      co: 'Dachdeckerei Ohlsen',
      meta: 'Dachdeckerei · Norderstedt · 10–30 Mitarbeitende',
      name: 'Lars Ohlsen',
      role: 'Inhaber',
      ap: 4,
    },
    {
      in: 'TV',
      co: 'Tischlerei Vandenberg',
      meta: 'Innenausbau · Hamburg · 12–35 Mitarbeitende',
      name: 'Nils Vandenberg',
      role: 'Inhaber',
      ap: 5,
    },
    {
      in: 'BW',
      co: 'Bau & Technik Wendt',
      meta: 'Bauunternehmen · Buxtehude · 30–70 Mitarbeitende',
      name: 'Frank Wendt',
      role: 'Geschäftsführer',
      ap: 10,
    },
  ],
  generic: [
    {
      in: 'NI',
      co: 'Nordmann Industrietechnik',
      meta: 'Maschinenbau · Hannover · 50–120 Mitarbeitende',
      name: 'Robert Nordmann',
      role: 'Geschäftsführer',
      email: 'r.nordmann@nordmann-it.de',
      phone: '+49 511 47 90 22-0',
      ap: 14,
    },
    {
      in: 'VS',
      co: 'Vogel & Schwarz GmbH',
      meta: 'B2B-Dienstleistung · Stuttgart · 20–50 Mitarbeitende',
      name: 'Julia Vogel',
      role: 'Inhaberin',
      ap: 8,
    },
    {
      in: 'MS',
      co: 'Meridian Solutions',
      meta: 'IT-Dienstleistung · Frankfurt · 30–80 Mitarbeitende',
      name: 'Daniel Krüger',
      role: 'Geschäftsführer',
      ap: 11,
    },
    {
      in: 'HT',
      co: 'Hanseatic Trade Group',
      meta: 'Großhandel · Bremen · 40–90 Mitarbeitende',
      name: 'Markus Albrecht',
      role: 'Geschäftsführer',
      ap: 9,
    },
    {
      in: 'AC',
      co: 'Alpenland Consulting',
      meta: 'Unternehmensberatung · Leipzig · 15–35 Mitarbeitende',
      name: 'Sandra Bauer',
      role: 'Partnerin',
      ap: 6,
    },
  ],
};

// ─── Ableitungen ──────────────────────────────────────────────────────────

/**
 * Keyword-Mapping query → Nische. 1:1-Port von `leadSet()`.
 * Fällt bei fehlendem Match auf 'generic' zurück.
 */
export function leadSetFor(query: string): Lead[] {
  const q = (query || '').toLowerCase();
  if (/steuer|kanzlei|wirtschaftspr|\bstb\b|\bwp\b/.test(q)) return LEAD_SETS.steuer;
  if (/agentur|marketing|werbe|design|kreativ|social/.test(q)) return LEAD_SETS.agentur;
  if (/handwerk|\bbau\b|elektro|sanit|heizung|dachdeck|maler|tischler|schreiner/.test(q))
    return LEAD_SETS.handwerk;
  return LEAD_SETS.generic;
}

/**
 * Saisonalitäts-Chart: der aktuelle Monat ist immer der Peak (100%),
 * mit zyklischem Abfall nach beiden Seiten (min. 32%). 1:1-Port von
 * `monthBars()`.
 */
export function monthBars(): MonthBar[] {
  const now = new Date().getMonth();
  return Array.from({ length: 12 }, (_, i) => {
    let d = Math.abs(i - now);
    d = Math.min(d, 12 - d);
    const h = Math.max(32, 100 - d * 12);
    return {
      label: t(`prism.month.short.${i + 1}`),
      hpct: `${h}%`,
      cls: i === now ? 'wu-mbar now' : 'wu-mbar',
      cap: i === now ? t('prism.month.now') : '',
    };
  });
}

/**
 * "Warum jetzt"-Fließtext — nennt den aktuellen Monat als beste
 * Ansprechphase und den Folgemonat als Wettbewerbs-Fenster. 1:1-Port
 * von `timingText()`.
 */
export function timingText(): string {
  const now = new Date().getMonth();
  // LEA-379 i18n#2 (Om QA): always the TRANSLATED generic — never the raw typed
  // target. A non-DE visitor typing a German audience string previously leaked it
  // verbatim into the localized timing sentence ("…la joignabilité chez
  // Mittelständler in Norddeutschland…"). The specific audience is already shown
  // verbatim elsewhere via `audienceQuote` (»…«), so nothing is lost here.
  const who = t('prism.audienceFallback');
  return t('prism.timing.text', {
    month: t(`prism.month.long.${now + 1}`),
    who,
    next: t(`prism.month.long.${((now + 1) % 12) + 1}`),
  });
}

// ─── Lead-Ableitungen (Favicon + Fit-Score) ────────────────────────────────
//
// Stufe 1 arbeitet auf Mock-Leads. In Stufe 2 kommen `domain` (verifiziert)
// und `score` (Fit aus dem Reasoning) aus der echten Pipeline — die Render-
// Komponente bleibt identisch, nur die Datenquelle wechselt.

/** Domain für Favicon/Link: explizit → aus E-Mail → aus Firmenname geslugged. */
export function leadDomain(l: Lead): string {
  if (l.domain) return l.domain;
  if (l.email?.includes('@')) return l.email.split('@')[1];
  const slug = l.co
    .toLowerCase()
    .replace(/&/g, 'und')
    .replace(/[äöü]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue' })[c] ?? c)
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug}.de`;
}

/** Favicon-URL (DuckDuckGo — privacy-freundlich, kein Google-Tracking). */
export function faviconUrl(domain: string): string {
  return `https://icons.duckduckgo.com/ip3/${domain}.ico`;
}

/**
 * Deterministischer Fit-Score im Premium-Band 87–97. Stabil pro Firma
 * (Hash über den Namen), damit er zwischen Rendern nicht springt. Der jeweils
 * erste Lead eines Sets (der voll enrichte Teaser) wird gezielt hoch gehalten.
 */
export function leadScore(l: Lead, isTop = false): number {
  let h = 0;
  for (let i = 0; i < l.co.length; i++) h = (h * 31 + l.co.charCodeAt(i)) >>> 0;
  if (isTop) return 94 + (h % 4); // 94–97
  return 87 + (h % 8); // 87–94
}

/** Label + Ampel-Klasse zum Score (nur Optik). */
export function scoreTier(score: number): { label: string; cls: string } {
  // Die kuratierte Liste zeigt fast nur fit ≥ 55 (ON_TARGET) und ist vom
  // unabhängigen Audit bestätigt — JEDER gezeigte Lead ist ein starker Treffer.
  // Deshalb KEIN „guter Fit" mehr (wirkt lauwarm, Janik 07.07.): die Skala
  // differenziert nur noch stark → top → perfekt, und ALLES ist grün — die
  // Ergebnisse sollen heiß machen, nicht mittelmäßig (amber) aussehen.
  if (score >= 85) return { label: t('prism.score.perfect'), cls: 'is-top' };
  if (score >= 68) return { label: t('prism.score.outstanding'), cls: 'is-high' };
  // Unter der eigenen on-target-Linie (55) nicht „sehr stark“ nennen.
  if (score < 55) return { label: t('prism.score.possible'), cls: 'is-mid' };
  return { label: t('prism.score.veryStrong'), cls: 'is-mid' };
}

// ── Seeded Live-Metriken ────────────────────────────────────────────────────
// Die Scan-Animation + Ergebnis-Statistiken brauchen große Kennzahlen, BEVOR
// (bzw. neben) den echten Engine-Werten. Statt hartcodierter Design-Werte
// (immer „3.412") werden sie deterministisch aus der Zielgruppen-Eingabe
// geseedet: pro Suche verschieden, bei derselben Suche stabil, in sich
// konsistent (AP ≈ 7×Firmen, verifiziert ≈ 86% der AP, Umsatz skaliert mit).
// Klar als Schätzungen geframt („~", „Schätzung") — echte harte Zahlen (geprüfte
// Firmen, Fit-Scores) kommen weiterhin ausschließlich aus der Engine.
export type SeededMetrics = { universe: number; ap: number; verified: number; revenueM: number };

function hashSeed(s: string): number {
  let h = 2166136261;
  for (const ch of s) {
    h ^= ch.codePointAt(0) ?? 0;
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('de-DE');
}

export function seededMetrics(query: string): SeededMetrics {
  const r = mulberry(hashSeed(query.trim().toLowerCase() || 'leadscraper'));
  const universe = Math.round((2400 + r() * 6200) / 10) * 10; // 2.400–8.600, auf 10er
  const ap = Math.round((universe * (6.4 + r() * 2.4)) / 100) * 100; // ≈ 7×
  const verified = Math.round((ap * (0.83 + r() * 0.07)) / 100) * 100; // ≈ 86%
  const revenueM = Math.round(universe * (0.07 + r() * 0.05)); // Mio €
  return { universe, ap, verified, revenueM };
}

/**
 * Scan-Schritte. Die Texte beschreiben nur, was die Engine vor dem Kauf wirklich tut
 * (Planung, Web-/Kartensuche, Filter, Bewertung der besten Treffer, Website-Check,
 * Plausibilität). Die früheren query-geseedeten Zahlen je Schritt (AP, „gültig“,
 * Umsatzpotenzial) waren Hash-Werte ohne Datenbasis und sind entfernt (29.09.2026).
 */
export function searchStepsFor(): FunnelStep[] {
  return [
    { t: t('prism.search.step1'), n: '' },
    { t: t('prism.search.step2'), n: '' },
    { t: t('prism.search.step3'), n: '' },
    { t: t('prism.search.step4'), n: '' },
    { t: t('prism.search.step5'), n: '' },
    { t: t('prism.search.step6'), n: '' },
    { t: t('prism.search.step7'), n: '' },
    { t: t('prism.search.step8'), n: '' },
    { t: t('prism.search.step9'), n: '' },
  ];
}
